// Cấu hình trang chủ (UI-first, localStorage): thông báo công ty + xác nhận đã đọc, nhóm theo vai trò, chỉ tiêu
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import dayjs from 'dayjs';
import { DEFAULT_TARGETS, type Announcement, type HomeRole, type RoleTarget } from './homeData';

interface HomeState {
  announcements: Announcement[];
  /** annId → userKey → thời điểm xác nhận đã đọc */
  acks: Record<string, Record<string, { at: string; name: string }>>;
  /** Tên vai trò hệ thống → nhóm trang chủ (ghi đè đoán tự động) */
  roleMap: Record<string, HomeRole>;
  targets: Record<HomeRole, RoleTarget>;
  /** Chỉ tiêu riêng từng người (userKey) */
  personalTargets: Record<string, number>;
  /** Super admin xem trước trang chủ của nhóm khác */
  viewAs: HomeRole | null;
  /** Phiên bản thông báo mẫu đã nạp (dev) */
  seedVer: number;
  seq: number;

  saveAnnouncement: (a: Omit<Announcement, 'id' | 'createdAt'> & { id?: string }) => string;
  removeAnnouncement: (id: string) => void;
  ack: (id: string, userKey: string, name: string) => void;
  setRoleMap: (roleName: string, role: HomeRole | null) => void;
  setTarget: (role: HomeRole, value: number) => void;
  setPersonalTarget: (userKey: string, value: number | null) => void;
  setViewAs: (role: HomeRole | null) => void;
  seedDemo: (by: string) => void;
  clearDemo: () => void;
}

export const DEMO_SEED_VER = 2;

const demoAnnouncements = (by: string, now = dayjs()): Announcement[] => [
  {
    id: 'demo-ann-1', title: 'Chương trình tri ân 20/11 – giảm 20% liệu trình chăm sóc da',
    content: 'Áp dụng từ 01/11 đến 30/11 cho mọi khách hàng mua thẻ liệu trình chăm sóc da.\nKhông áp dụng đồng thời với ưu đãi hạng thành viên. Sale vui lòng báo giá theo bảng giá khuyến mãi mới trên POS.',
    level: 'event', pinned: true, requireAck: false, audience: [], until: now.add(40, 'day').format('YYYY-MM-DD'),
    createdAt: now.subtract(1, 'day').hour(8).minute(30).toISOString(), by, demo: true,
  },
  {
    id: 'demo-ann-2', title: 'Cập nhật phác đồ laser trị nám (bản 10/2026)',
    content: 'Thay đổi thông số năng lượng buổi 1–3 và thời gian nghỉ giữa các buổi từ 21 lên 28 ngày.\nBác sĩ / KTV đọc tài liệu đính kèm ở mục Quản lý tài liệu và xác nhận trước ca làm.',
    level: 'policy', pinned: false, requireAck: true, audience: ['doctor', 'manager'], until: '',
    createdAt: now.subtract(2, 'day').hour(17).toISOString(), by, demo: true,
  },
  {
    id: 'demo-ann-3', title: 'Quy định mới: khách ký xác nhận mỗi lần đến',
    content: 'Từ nay mọi lần khách làm dịch vụ đều phải ký phiếu xác nhận khi ra về (ký trên máy hoặc phiếu giấy).\nLần đến chưa ký quá 3 ngày sẽ báo về quản lý cơ sở.',
    level: 'urgent', pinned: true, requireAck: true, audience: [], until: '',
    createdAt: now.subtract(4, 'day').hour(9).toISOString(), by, demo: true,
  },
  {
    id: 'demo-ann-4', title: 'Kịch bản gọi chăm sóc sau dịch vụ tháng 11',
    content: 'Gọi lại khách sau 3 ngày và 14 ngày kể từ buổi làm. Ghi kết quả cuộc gọi vào Lịch CSKH.',
    level: 'info', pinned: false, requireAck: false, audience: ['telesale'], until: '',
    createdAt: now.subtract(3, 'day').hour(14).toISOString(), by, demo: true,
  },
  {
    id: 'demo-ann-5', title: 'Họp giao ban toàn công ty thứ 6 lúc 17:30',
    content: 'Phòng họp tầng 3 hoặc tham gia qua Khothi Meeting. Các quản lý cơ sở chuẩn bị báo cáo doanh số tháng.',
    level: 'info', pinned: false, requireAck: false, audience: [], until: now.add(3, 'day').format('YYYY-MM-DD'),
    createdAt: now.subtract(5, 'hour').toISOString(), by, demo: true,
  },
  {
    id: 'demo-ann-6', title: 'Cảnh báo: không cung cấp mã OTP, mật khẩu cho bất kỳ ai',
    content: 'Gần đây có đối tượng giả danh phòng IT gọi điện xin mã OTP đăng nhập hệ thống.\nCông ty không bao giờ yêu cầu mã OTP hay mật khẩu qua điện thoại, Zalo. Gặp trường hợp này báo ngay cho quản lý.',
    level: 'urgent', pinned: false, requireAck: true, audience: [], until: '',
    createdAt: now.subtract(50, 'minute').toISOString(), by: 'Phòng IT', demo: true,
  },
  {
    id: 'demo-ann-7', title: 'Đào tạo sản phẩm mới: Filler HA thế hệ 4 – sáng thứ 7',
    content: '08:30–11:30 thứ 7 tuần này tại phòng đào tạo tầng 2. Sale và Bác sĩ / KTV bắt buộc tham dự, mang theo sổ ghi chép.',
    level: 'event', pinned: false, requireAck: false, audience: ['sale', 'doctor'], until: now.add(5, 'day').format('YYYY-MM-DD'),
    createdAt: now.subtract(1, 'day').hour(15).toISOString(), by: 'Phòng Đào tạo', demo: true,
  },
  {
    id: 'demo-ann-8', title: 'Thưởng nóng tháng 10 cho top 3 Sale và Telesale',
    content: 'Top 1: 5 triệu · Top 2: 3 triệu · Top 3: 2 triệu. Xếp hạng theo doanh số và tỉ lệ khách đến, chốt số ngày 31/10.',
    level: 'event', pinned: false, requireAck: false, audience: ['sale', 'telesale', 'manager'], until: now.endOf('month').format('YYYY-MM-DD'),
    createdAt: now.subtract(3, 'day').hour(10).toISOString(), by, demo: true,
  },
  {
    id: 'demo-ann-9', title: 'Lịch nghỉ Tết Dương lịch 2027',
    content: 'Toàn công ty nghỉ ngày 01/01/2027. Các cơ sở bố trí trực theo lịch phòng Nhân sự gửi qua email.',
    level: 'info', pinned: false, requireAck: false, audience: [], until: '2027-01-02',
    createdAt: now.subtract(6, 'day').hour(9).toISOString(), by: 'Phòng Nhân sự', demo: true,
  },
];

const useHomeStore = create<HomeState>()(
  persist(
    (set, get) => ({
      announcements: [],
      acks: {},
      roleMap: {},
      targets: DEFAULT_TARGETS,
      personalTargets: {},
      viewAs: null,
      seedVer: 0,
      seq: 0,

      saveAnnouncement: (a) => {
        const s = get();
        const id = a.id || `ann-${Date.now().toString(36)}-${s.seq + 1}`;
        const prev = s.announcements.find(x => x.id === id);
        const next: Announcement = { ...a, id, createdAt: prev?.createdAt || new Date().toISOString() };
        set({ seq: s.seq + 1, announcements: prev ? s.announcements.map(x => (x.id === id ? next : x)) : [next, ...s.announcements] });
        return id;
      },
      removeAnnouncement: (id) => set(s => {
        const { [id]: _, ...acks } = s.acks;
        return { announcements: s.announcements.filter(a => a.id !== id), acks };
      }),
      ack: (id, userKey, name) => set(s => ({ acks: { ...s.acks, [id]: { ...(s.acks[id] || {}), [userKey]: { at: new Date().toISOString(), name } } } })),
      setRoleMap: (roleName, role) => set(s => {
        const { [roleName]: _, ...rest } = s.roleMap;
        return { roleMap: role ? { ...rest, [roleName]: role } : rest };
      }),
      setTarget: (role, value) => set(s => ({ targets: { ...s.targets, [role]: { ...s.targets[role], value } } })),
      setPersonalTarget: (userKey, value) => set(s => {
        const { [userKey]: _, ...rest } = s.personalTargets;
        return { personalTargets: value ? { ...rest, [userKey]: value } : rest };
      }),
      setViewAs: (viewAs) => set({ viewAs }),
      seedDemo: (by) => set(s => {
        const ids = new Set(s.announcements.map(a => a.id));
        return { seedVer: DEMO_SEED_VER, announcements: [...s.announcements, ...demoAnnouncements(by).filter(a => !ids.has(a.id))] };
      }),
      clearDemo: () => set(s => {
        const demoIds = new Set(s.announcements.filter(a => a.demo).map(a => a.id));
        return {
          announcements: s.announcements.filter(a => !a.demo),
          acks: Object.fromEntries(Object.entries(s.acks).filter(([id]) => !demoIds.has(id))),
        };
      }),
    }),
    {
      name: 'khothi-home-v1',
      merge: (persisted, current) => {
        const p = (persisted || {}) as Partial<HomeState>;
        return { ...current, ...p, targets: { ...current.targets, ...(p.targets || {}) } };
      },
    },
  ),
);

export default useHomeStore;
